/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, Eye, EyeOff, Check, X, User, Phone } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../services/api";
import Cookies from "js-cookie";
import { EMAIL_ONLY_SIGNUP } from "../constants/signup";

const SignUp = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
    criteria: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    }
  });
  const navigate = useNavigate();
  const visitorInfoRef = useRef({ ip: '', country: '' });

  // Password strength evaluation function
  const evaluatePasswordStrength = (password) => {
    const criteria = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const score = Object.values(criteria).filter(Boolean).length;
    let feedback = "";
    let strengthClass = "";

    if (password.length === 0) {
      feedback = "";
    } else if (score <= 2) {
      feedback = "Weak password";
      strengthClass = "text-red-500";
    } else if (score <= 3) {
      feedback = "Fair password";
      strengthClass = "text-yellow-500";
    } else if (score <= 4) {
      feedback = "Good password";
      strengthClass = "text-blue-500";
    } else {
      feedback = "Strong password";
      strengthClass = "text-green-500";
    }

    return {
      score,
      feedback,
      strengthClass,
      criteria
    };
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(evaluatePasswordStrength(newPassword));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (EMAIL_ONLY_SIGNUP && (!name || !email || !phone)) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!EMAIL_ONLY_SIGNUP && !email) {
      toast.error("Please enter your email address");
      return;
    }

    if (!EMAIL_ONLY_SIGNUP && !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      if (EMAIL_ONLY_SIGNUP) {
        const [, response] = await Promise.all([
          new Promise((resolve) => setTimeout(resolve, 600)),
          api.signupWithEmail({
            name,
            email,
            phone,
            ip: visitorInfoRef.current.ip,
            country: visitorInfoRef.current.country,
          }),
        ]);
        if (response.status === "success") {
          setSubmitted(true);
        } else {
          toast.error(response.message || "Failed to create account");
        }
      } else {
        const response = await api.signup({ email, password });
        if (response.status === "success") {
          navigate("/verify", { state: { user: response.data, email, password } });
        } else {
          toast.error(response.message);
        }
      }
    } catch (error) {
      toast.error("Failed to create account");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    try {
      const allCookies = Cookies.get();
      for (const cookieName in allCookies) {
        Cookies.remove(cookieName);
      }
      const response = await api.googleSignup();
      window.location.href = response;
    } catch (error) {
      toast.error("Google signup failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/user-details');
    }
  }, [navigate]);

  useEffect(() => {
    api.getVisitorInfo().then((info) => {
      visitorInfoRef.current = info;
    });
  }, []);

  useEffect(() => {
    if (!submitted) return undefined;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = "https://salescaptain.com/";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [submitted]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img
            src="https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2025-03-18/full_logo.a46683a620fa53c3c554_1742299090688.png"
            alt="SalesCaptain Logo"
            className="h-10 object-contain"
          />
        </div>

        {submitted ? (
          <div className="text-center space-y-6">
            <h1 className="text-2xl font-semibold mb-2">Thank you</h1>
            <p className="text-gray-500">
              Our sales team will contact you shortly.
            </p>
            <p className="text-gray-500 text-sm">
              Redirecting to our website in {countdown} second{countdown === 1 ? "" : "s"}...
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = "https://salescaptain.com/";
              }}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Click to redirect now
            </button>
          </div>
        ) : (
          <>
        <h1 className="text-2xl font-semibold text-center mb-2">
          Create your free account
        </h1>
        <p className="text-gray-500 text-center mb-8">
          Sign up to get started with SalesCaptain
        </p>

        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-4">
            {EMAIL_ONLY_SIGNUP && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                required
                disabled={loading}
              />
            </div>

            {EMAIL_ONLY_SIGNUP && (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  required
                  disabled={loading}
                />
              </div>
            )}

            {!EMAIL_ONLY_SIGNUP && (
              <div className="space-y-2">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="pl-10 pr-12 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5"
                  >
                    {showPassword ? (
                      <Eye className="h-5 w-5" />
                    ) : (
                      <EyeOff className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {password && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          passwordStrength.score <= 2
                            ? "bg-red-500"
                            : passwordStrength.score <= 3
                            ? "bg-yellow-500"
                            : passwordStrength.score <= 4
                            ? "bg-blue-500"
                            : "bg-green-500"
                        }`}
                        style={{
                          width: `${(passwordStrength.score / 5) * 100}%`,
                        }}
                      ></div>
                    </div>

                    <p className={`text-sm font-medium ${passwordStrength.strengthClass}`}>
                      {passwordStrength.feedback}
                    </p>

                    <div className="text-xs space-y-1 grid grid-cols-2 gap-2">
                      {!passwordStrength.criteria.length && (
                        <div className="flex items-center gap-2">
                          <X className="h-3 w-3 text-red-500" />
                          <span className="text-gray-500">
                            At least 8 characters
                          </span>
                        </div>
                      )}
                      {!passwordStrength.criteria.uppercase && (
                        <div className="flex items-center gap-2">
                          <X className="h-3 w-3 text-red-500" />
                          <span className="text-gray-500">
                            One uppercase letter
                          </span>
                        </div>
                      )}
                      {!passwordStrength.criteria.lowercase && (
                        <div className="flex items-center gap-2">
                          <X className="h-3 w-3 text-red-500" />
                          <span className="text-gray-500">
                            One lowercase letter
                          </span>
                        </div>
                      )}
                      {!passwordStrength.criteria.number && (
                        <div className="flex items-center gap-2">
                          <X className="h-3 w-3 text-red-500" />
                          <span className="text-gray-500">
                            One number
                          </span>
                        </div>
                      )}
                      {!passwordStrength.criteria.special && (
                        <div className="flex items-center gap-2">
                          <X className="h-3 w-3 text-red-500" />
                          <span className="text-gray-500">
                            One special character
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            className={`w-full h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center ${
              loading
                ? "opacity-60 cursor-not-allowed"
                : "hover:bg-blue-700"
            }`}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </button>

          {!EMAIL_ONLY_SIGNUP && (
            <>
              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-sm">or</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <button
                type="button"
                className="w-full h-12 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                onClick={handleGoogleSignUp}
                disabled={loading}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                Continue with Google
              </button>
            </>
          )}
        </form>

        <div className="mt-8 text-center text-sm">
          <p className="text-gray-500">
            Already have an account?{" "}
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Log In
            </Link>
          </p>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          By continuing, you acknowledge and accept our{" "}
          <a href="https://salescaptain.com/term-and-conditions" target="_blank" className="text-blue-600 hover:text-blue-800">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="https://salescaptain.com/privacy-policy" target="_blank" className="text-blue-600 hover:text-blue-800">
            Privacy Policy
          </a>
          .
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignUp;
